// src/components/family/ChildCard.tsx
// Display card for a child with avatar, info, diagnoses, and quick actions

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Heart,
  FileText,
  MoreVertical,
  Edit2,
  Trash2,
  Activity,
  User,
  Stethoscope,
} from 'lucide-react';
import type { Child } from '@/features/children/types/childTypes';
import {
  formatAge,
  getChildInitials,
  getAvatarGradient,
  getPrimaryDiagnosis,
} from '@/features/children/types/childTypes';

interface ChildCardProps {
  child: Child;
  familyId: string;
  onEdit?: (child: Child) => void;
  onDelete?: (child: Child) => void;
  variant?: 'grid' | 'list';
}

export function ChildCard({
  child,
  familyId,
  onEdit,
  onDelete,
  variant = 'grid',
}: ChildCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const initials = getChildInitials(child);
  const avatarGradient = getAvatarGradient(child.first_name + child.last_name);
  const primaryDiagnosis = getPrimaryDiagnosis(child);
  const age = formatAge(child.date_of_birth);
  const diagnosisCount = child.diagnoses?.length ?? 0;

  const handleCardClick = () => {
    navigate(`/family/${familyId}/children/${child.id}`);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    onEdit?.(child);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDelete?.(child);
  };

  const handleQuickAction = (e: React.MouseEvent, action: string) => {
    e.stopPropagation();
    switch (action) {
      case 'health':
        navigate(`/family/${familyId}/children/${child.id}/health`);
        break;
      case 'documents':
        navigate(`/family/${familyId}/children/${child.id}/documents`);
        break;
      case 'calendar':
        navigate(`/family/${familyId}/children/${child.id}/calendar`);
        break;
    }
  };

  if (variant === 'list') {
    return (
      <div
        onClick={handleCardClick}
        className="group flex items-center gap-4 p-4 bg-white rounded-xl border border-warm-gray/10 hover:border-sage/30 hover:shadow-soft transition-all duration-300 cursor-pointer"
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {child.avatar_url ? (
            <img
              src={child.avatar_url}
              alt={`${child.first_name}'s photo`}
              className="w-14 h-14 rounded-full object-cover ring-2 ring-cream"
            />
          ) : (
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white font-display font-semibold text-lg"
              style={{ background: avatarGradient }}
            >
              {initials}
            </div>
          )}
          {diagnosisCount > 0 && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-sage rounded-full flex items-center justify-center">
              <Stethoscope className="w-3 h-3 text-white" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-navy truncate">
              {child.first_name} {child.last_name}
            </h3>
            {child.nickname && (
              <span className="text-sm text-warm-gray">({child.nickname})</span>
            )}
          </div>
          <p className="text-sm text-warm-gray">{age}</p>
        </div>

        {/* Diagnosis Badge */}
        {primaryDiagnosis && (
          <div className="hidden sm:block">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-sage/10 text-sage-dark">
              {primaryDiagnosis}
              {diagnosisCount > 1 && (
                <span className="ml-1 text-sage">+{diagnosisCount - 1}</span>
              )}
            </span>
          </div>
        )}

        {/* Quick Actions */}
        <div className="hidden md:flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => handleQuickAction(e, 'health')}
            className="p-2 rounded-lg hover:bg-sage/10 text-warm-gray hover:text-sage transition-colors"
            title="Health Tracking"
          >
            <Activity className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => handleQuickAction(e, 'documents')}
            className="p-2 rounded-lg hover:bg-sage/10 text-warm-gray hover:text-sage transition-colors"
            title="Documents"
          >
            <FileText className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => handleQuickAction(e, 'calendar')}
            className="p-2 rounded-lg hover:bg-sage/10 text-warm-gray hover:text-sage transition-colors"
            title="Calendar"
          >
            <Calendar className="w-4 h-4" />
          </button>
        </div>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="p-2 rounded-lg hover:bg-cream text-warm-gray hover:text-navy transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                }}
              />
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-medium border border-warm-gray/10 py-1 z-20">
                <button
                  onClick={handleEditClick}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-cream transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-warm-gray" />
                  Edit Profile
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-red-50 text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Grid variant (default)
  return (
    <div
      onClick={handleCardClick}
      className="group relative bg-white rounded-2xl border border-warm-gray/10 overflow-hidden hover:border-sage/30 hover:shadow-lifted transition-all duration-400 cursor-pointer"
    >
      {/* Accent Bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-400"
        style={{ background: avatarGradient }}
      />

      {/* Header with Avatar */}
      <div className="relative p-6 pb-4">
        {/* Menu Button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="p-2 rounded-lg hover:bg-cream text-warm-gray hover:text-navy opacity-0 group-hover:opacity-100 transition-all"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                }}
              />
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-medium border border-warm-gray/10 py-1 z-20">
                <button
                  onClick={handleEditClick}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-cream transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-warm-gray" />
                  Edit Profile
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-red-50 text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
            </>
          )}
        </div>

        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div className="relative mb-3">
            {child.avatar_url ? (
              <img
                src={child.avatar_url}
                alt={`${child.first_name}'s photo`}
                className="w-20 h-20 rounded-full object-cover ring-4 ring-cream group-hover:ring-sage/20 transition-all"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white font-display font-semibold text-2xl ring-4 ring-cream group-hover:ring-sage/20 transition-all"
                style={{ background: avatarGradient }}
              >
                {initials}
              </div>
            )}
            {/* Online indicator or status could go here */}
          </div>

          {/* Name */}
          <h3 className="font-display font-semibold text-lg text-navy text-center">
            {child.first_name} {child.last_name}
          </h3>
          {child.nickname && (
            <p className="text-sm text-warm-gray text-center">"{child.nickname}"</p>
          )}

          {/* Age */}
          <p className="mt-1 text-sm text-warm-gray flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {age}
          </p>
        </div>
      </div>

      {/* Diagnoses */}
      {diagnosisCount > 0 && (
        <div className="px-6 pb-4">
          <div className="flex flex-wrap justify-center gap-2">
            {child.diagnoses?.slice(0, 2).map((diagnosis) => (
              <span
                key={diagnosis.id}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  diagnosis.is_primary
                    ? 'bg-sage/15 text-sage-dark'
                    : 'bg-cream text-navy-light'
                }`}
              >
                {diagnosis.diagnosis.short_name || diagnosis.diagnosis.name}
              </span>
            ))}
            {diagnosisCount > 2 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cream text-warm-gray">
                +{diagnosisCount - 2} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-warm-gray/10" />

      {/* Quick Actions */}
      <div className="grid grid-cols-3 divide-x divide-warm-gray/10">
        <button
          onClick={(e) => handleQuickAction(e, 'health')}
          className="flex flex-col items-center py-3 hover:bg-sage/5 transition-colors group/action"
        >
          <Heart className="w-4 h-4 text-warm-gray group-hover/action:text-sage transition-colors" />
          <span className="mt-1 text-xs text-warm-gray group-hover/action:text-sage-dark transition-colors">
            Health
          </span>
        </button>
        <button
          onClick={(e) => handleQuickAction(e, 'documents')}
          className="flex flex-col items-center py-3 hover:bg-sage/5 transition-colors group/action"
        >
          <FileText className="w-4 h-4 text-warm-gray group-hover/action:text-sage transition-colors" />
          <span className="mt-1 text-xs text-warm-gray group-hover/action:text-sage-dark transition-colors">
            Documents
          </span>
        </button>
        <button
          onClick={(e) => handleQuickAction(e, 'calendar')}
          className="flex flex-col items-center py-3 hover:bg-sage/5 transition-colors group/action"
        >
          <Calendar className="w-4 h-4 text-warm-gray group-hover/action:text-sage transition-colors" />
          <span className="mt-1 text-xs text-warm-gray group-hover/action:text-sage-dark transition-colors">
            Calendar
          </span>
        </button>
      </div>
    </div>
  );
}

export default ChildCard;

